import React, { useEffect, useState } from 'react';
import { inject } from 'mobx-react';
import { useWindowSize } from 'common/hooks';
import Breadcrumbs from 'public/screens/CustomLandingPage/Components/Breadcrumbs';
import {
  getContentfulAssetUrlandAlt,
  getContentfulField,
} from 'common/components';
import {
  parseAttraction,
  parseTicketOptions,
} from 'public/helpers/contentful/parser';
import { getContentRides } from 'public/components/AttractionScreen/RideClosures';
import useCustomStyle from 'public/screens/CustomLandingPage/hooks';
import { isVenueDateUnavailable } from 'utils/helpers/openingHours';

const SubNav = ({ venue, openingHoursBanner }) => {
  const { isMobile, isTablet, isDesktop } = useWindowSize();
  const breadcrumb = getContentfulField(venue?.parkScreen?.breadcrumb);
  const [unavailableDates, setUnavailableDates] = useState([]);

  useEffect(() => {
    if (!venue) return;
    const unavailableDates = venue?.priceTypes?.reduce(
      (acc, { date, type }) => (type === 'closed' ? acc.concat(date) : acc),
      []
    );
    setUnavailableDates(unavailableDates);
  }, [venue]);

  const items = getContentfulField(breadcrumb.fields.items).map((item) => {
    const fields = item.fields || {};

    return {
      ...item,
      fields: {
        ...fields,
        key: getContentfulField(fields.key),
        value: getContentfulField(fields.value),
        type: getContentfulField(fields.type),
        label: getContentfulField(fields.label),
        url: getContentfulField(fields.url),
        isPdf: getContentfulField(fields.isPdf),
        theme: getContentfulField(fields.theme),
        icon: fields.icon ? getContentfulAssetUrlandAlt(fields.icon) : null,
        settings: getContentfulField(fields.settings),
      },
    };
  });

  const breadcrumbs = {
    ...breadcrumb,
    fields: {
      ...breadcrumb.fields,
      items,
      type: getContentfulField(breadcrumb.fields.type),
      name: getContentfulField(breadcrumb.fields.name),
    },
  };

  const isDateUnavailable = (isoDate) =>
    isVenueDateUnavailable(venue, isoDate, unavailableDates);

  const venueParkAccessMessage = getContentfulField(
    venue?.parkScreen?.parkAccessMessage
  );

  const ticketOptions = venue.ticketOptions
    ? parseTicketOptions(venue.ticketOptions)
    : venue.content?.attractionScreen
    ? parseAttraction(venue.content.attractionScreen)?.ticketOptions
    : [];

  const ridesCards = getContentRides(venue);

  const openingHoursBannerFields = getContentfulField(
    venue.parkScreen.openingHoursBanner
  )?.fields;
  const emptyAsset = getContentfulField(openingHoursBannerFields?.logoOverlay);
  const settingsField = getContentfulField(openingHoursBannerFields?.settings);

  const rawSettings =
    settingsField?.mobile || settingsField?.tablet || settingsField?.desktop
      ? (isMobile && settingsField.mobile) ||
        (isTablet && settingsField.tablet) ||
        (isDesktop && settingsField.desktop)
      : settingsField;

  const processedSettings = (Array.isArray(rawSettings) ? rawSettings : []).map(
    (setting) => ({
      ...setting,
      fields: {
        key: getContentfulField(setting.fields?.key),
        name: getContentfulField(setting.fields?.name),
        value: getContentfulField(setting.fields?.value),
        mobileValue: getContentfulField(setting.fields?.mobileValue),
        tabletValue: getContentfulField(setting.fields?.tabletValue),
        desktopValue: getContentfulField(setting.fields?.desktopValue),
      },
    })
  );

  const [, style] = useCustomStyle(processedSettings);
  const emptyAssetStyle = Object.entries(style).reduce((acc, [key, value]) => {
    if (key.startsWith('--logo-overlay-')) {
      acc[key.replace('--logo-overlay-', '')] = value;
    }
    return acc;
  }, {});

  return (
    <Breadcrumbs
      component={breadcrumbs}
      refs={[]}
      featuredFlags={venue.featuredFlags}
      openingHours={venue.openingHours}
      isDateUnavailable={isDateUnavailable}
      openingHoursBanner={openingHoursBanner}
      venueParkAccessMessage={venueParkAccessMessage}
      ticketOptions={ticketOptions}
      ridesCards={ridesCards}
      venueSlug={venue.slug}
      venueId={venue.id}
      emptyAsset={emptyAsset}
      emptyAssetStyle={emptyAssetStyle}
    />
  );
};

export default inject('betaFeatures')(SubNav);
